import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Globe, ExternalLink, X } from "lucide-react";

const LEATHER = "#6C7A89";
const BORDEAUX = "#2F496E";
const ACCENT = "#DCC6A3";
const CREAM = "#F7F4EE";
const MUTED = "#6C7A89";
const TEXT = "#2B2B2B";

// Réalisations livrées
const realisations = [
  {
    id: "lebonmatelot",
    name: "Le Bon Matelot",
    tagline: "Site vitrine · Bretagne",
    description:
      "Un site vitrine clair et engageant pour une activité locale bretonne.",
    url: "https://lebonmatelot.fr/",
    domain: "lebonmatelot.fr",
    image:
      "https://customer-assets-4nw71qhi.emergentagent.net/job_breizh-boost/artifacts/quwvv3ng_Le-Bon-Matelot-%E2%80%94-embarquer-avec-d-autres-pe%CC%82cheurs-10-02-2026_03_38_PM.webp",
    bg: "#F7F4EE",
  },
  {
    id: "hingantclean",
    name: "Hingant Clean",
    tagline: "Services de nettoyage · Côtes-d'Armor",
    description:
      "Un site professionnel pour valoriser une entreprise locale de nettoyage.",
    url: "https://hingantclean22.fr/",
    domain: "hingantclean22.fr",
    image:
      "https://customer-assets-4nw71qhi.emergentagent.net/job_breizh-boost/artifacts/c44hq4co_Hingant-Clean-%E2%80%94-Nettoyage-inte%CC%81rieur-de-ve%CC%81hicules-10-02-2026_03_38_PM.png",
    bg: "#F7F4EE",
  },
];

// Projets en cours
const projetsEnCours = [
  {
    id: "tamaris",
    name: "Camping Les Tamaris",
    subtitle: "Refonte complète du site internet",
    tagline: "Camping · Plouha · Bretagne",
    shortDescription:
      "Refonte complète du site du Camping Les Tamaris à Plouha : nouvelle identité visuelle, nouvelle navigation, mise en valeur du camping et de ses hébergements, intégration de la réservation en ligne et valorisation de son environnement touristique.",
    axes: [
      {
        title: "Refonte du site",
        text: "Nouvelle structure, navigation et design responsive.",
      },
      {
        title: "Identité visuelle",
        text: "Couleurs, typographies et univers graphique adaptés au camping.",
      },
      {
        title: "Expérience client",
        text: "Présentation claire des emplacements, informations pratiques, snack, animations et réservation.",
      },
      {
        title: "Visibilité",
        text: "Intégration Google, avis, réseaux sociaux et mise en valeur de l'environnement touristique.",
      },
    ],
    tags: ["Site internet", "Identité visuelle", "Réservation", "Visibilité locale"],
    betaUrl: "https://tamaris-beta.preview.emergentagent.com/fr",
    image:
      "https://customer-assets-4nw71qhi.emergentagent.net/job_breizh-boost/artifacts/1t98e2ln_Camping-Les-Tamaris-%E2%80%94-Camping-face-a%CC%80-la-mer-a%CC%80-Plouha-anse-de-Bre%CC%81hec-Bretagne-10-02-2026_03_26_PM.png",
  },
];

function RealisationCard({ item }) {
  return (
    <article
      data-testid={`realisation-${item.id}`}
      className="hover-card group relative overflow-hidden rounded-3xl bg-up-offwhite border border-up-taupe/40 shadow-[0_28px_60px_-30px_rgba(47,73,110,0.28)]"
    >
      <div
        className="relative overflow-hidden flex items-center justify-center aspect-[16/10]"
        style={{ background: item.bg }}
      >
        <img
          src={item.image}
          alt={`Site ${item.name}`}
          className="relative w-full h-full object-contain transition-transform duration-700 group-hover:scale-105"
          loading="lazy"
        />
      </div>
      <div className="p-7 sm:p-8">
        <span
          className="text-[11px] tracking-[0.28em] uppercase font-medium"
          style={{ color: MUTED }}
        >
          {item.tagline}
        </span>
        <h3
          className="mt-2 font-serif text-2xl md:text-[1.75rem] leading-tight tracking-tight"
          style={{ color: BORDEAUX }}
        >
          {item.name}.
        </h3>
        <div
          className="mt-3 inline-flex items-center gap-1.5 text-[12px]"
          style={{ color: MUTED }}
        >
          <Globe className="h-3.5 w-3.5" />
          <span>{item.domain}</span>
        </div>
        <p className="mt-4 text-[14px] leading-[1.7]" style={{ color: TEXT }}>
          {item.description}
        </p>
        <a
          href={item.url}
          target="_blank"
          rel="noopener noreferrer"
          data-testid={`realisation-${item.id}-cta`}
          className="mt-6 self-start inline-flex items-center gap-2 rounded-full bg-up-brown text-up-offwhite px-6 py-3 text-sm font-medium tracking-wide transition-all duration-500 hover:-translate-y-0.5 hover:shadow-[0_18px_40px_-18px_rgba(47,73,110,0.55)]"
        >
          Voir le site
          <ExternalLink className="h-4 w-4" strokeWidth={1.8} />
        </a>
      </div>
    </article>
  );
}

function ProjetEnCoursCard({ item, onOpen }) {
  return (
    <article
      data-testid={`projet-${item.id}`}
      className="hover-card group relative overflow-hidden rounded-3xl bg-up-offwhite border border-up-taupe/40 shadow-[0_28px_60px_-30px_rgba(47,73,110,0.28)] cursor-pointer"
      onClick={onOpen}
    >
      {/* Visuel */}
      <div className="relative overflow-hidden aspect-[16/10]">
        <img
          src={item.image}
          alt={item.name}
          className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
          loading="lazy"
        />
        {/* Badge Projet en cours */}
        <div
          className="float-soft-delay absolute top-4 left-4 inline-flex items-center gap-2 rounded-full px-3.5 py-1.5 text-[10px] tracking-[0.26em] uppercase font-medium shadow-[0_14px_30px_-12px_rgba(47,73,110,0.5)] ring-1"
          style={{
            background: "#F7F4EE",
            color: "#2F496E",
            borderColor: "rgba(47,73,110,0.18)",
          }}
        >
          <span className="inline-flex h-1.5 w-1.5 rounded-full bg-amber-500 animate-pulse" />
          Projet en cours
        </div>
      </div>
      <div className="p-7 sm:p-8">
        <span
          className="text-[11px] tracking-[0.28em] uppercase font-medium"
          style={{ color: MUTED }}
        >
          {item.tagline}
        </span>
        <h3
          className="mt-2 font-serif text-2xl md:text-[1.75rem] leading-tight tracking-tight"
          style={{ color: BORDEAUX }}
        >
          {item.name}.
        </h3>
        <p
          className="mt-2 font-serif italic text-base md:text-lg"
          style={{ color: ACCENT }}
        >
          {item.subtitle}
        </p>
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onOpen();
          }}
          data-testid={`projet-${item.id}-open`}
          className="mt-6 inline-flex items-center gap-2 rounded-full bg-up-brown text-up-offwhite px-6 py-3 text-sm font-medium tracking-wide transition-all duration-500 hover:-translate-y-0.5 hover:shadow-[0_18px_40px_-18px_rgba(47,73,110,0.55)]"
        >
          En savoir plus
          <ArrowUpRight className="h-4 w-4" />
        </button>
      </div>
    </article>
  );
}

function ProjetModal({ item, onClose }) {
  useEffect(() => {
    const handler = (e) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handler);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", handler);
      document.body.style.overflow = "";
    };
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-4 sm:p-6 overflow-y-auto"
      data-testid="projet-modal"
      role="dialog"
      aria-modal="true"
      aria-labelledby="projet-modal-title"
    >
      <div
        className="absolute inset-0 bg-up-brown/70 backdrop-blur-sm"
        onClick={onClose}
      />
      <div
        className="relative my-8 w-full max-w-3xl rounded-3xl overflow-hidden shadow-[0_60px_120px_-30px_rgba(47,73,110,0.6)]"
        style={{ background: CREAM }}
      >
        <button
          type="button"
          onClick={onClose}
          aria-label="Fermer"
          data-testid="projet-modal-close"
          className="absolute top-4 right-4 z-10 inline-flex h-10 w-10 items-center justify-center rounded-full bg-up-offwhite/95 backdrop-blur ring-1 ring-up-brown/20 text-up-brown transition-all duration-300 hover:bg-up-brown hover:text-up-offwhite hover:scale-110"
        >
          <X className="h-4 w-4" strokeWidth={2} />
        </button>

        {/* Image */}
        <div className="relative aspect-[16/9] overflow-hidden">
          <img
            src={item.image}
            alt={item.name}
            className="w-full h-full object-cover"
          />
          <div
            className="absolute top-5 left-5 inline-flex items-center gap-2 rounded-full px-3.5 py-1.5 text-[10px] tracking-[0.26em] uppercase font-medium shadow-[0_14px_30px_-12px_rgba(47,73,110,0.5)] ring-1"
            style={{
              background: "#F7F4EE",
              color: "#2F496E",
              borderColor: "rgba(47,73,110,0.18)",
            }}
          >
            <span className="inline-flex h-1.5 w-1.5 rounded-full bg-amber-500 animate-pulse" />
            Projet en cours
          </div>
        </div>

        <div className="p-7 sm:p-10">
          <span
            className="text-[11px] tracking-[0.28em] uppercase font-medium"
            style={{ color: MUTED }}
          >
            {item.tagline}
          </span>
          <h2
            id="projet-modal-title"
            className="mt-2 font-serif text-[1.75rem] sm:text-3xl md:text-4xl leading-[1.1] tracking-tight"
            style={{ color: BORDEAUX }}
          >
            {item.name}.
          </h2>
          <p
            className="mt-2 font-serif italic text-lg md:text-xl"
            style={{ color: ACCENT }}
          >
            {item.subtitle}
          </p>

          <p
            className="mt-6 text-[14px] md:text-[15px] leading-[1.75]"
            style={{ color: TEXT }}
          >
            {item.shortDescription}
          </p>

          {item.axes && item.axes.length > 0 && (
            <div className="mt-8 grid sm:grid-cols-2 gap-5">
              {item.axes.map((axe) => (
                <div
                  key={axe.title}
                  className="rounded-2xl ring-1 px-5 py-4"
                  style={{
                    background: "rgba(220,198,163,0.15)",
                    borderColor: "rgba(47,73,110,0.15)",
                  }}
                >
                  <h4
                    className="font-serif text-base md:text-lg tracking-tight"
                    style={{ color: BORDEAUX }}
                  >
                    {axe.title}
                  </h4>
                  <p
                    className="mt-1.5 text-[13px] md:text-[14px] leading-[1.6]"
                    style={{ color: TEXT }}
                  >
                    {axe.text}
                  </p>
                </div>
              ))}
            </div>
          )}

          {item.tags && item.tags.length > 0 && (
            <div className="mt-7 flex flex-wrap gap-2">
              {item.tags.map((t) => (
                <span
                  key={t}
                  className="inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-[11px] tracking-wide ring-1"
                  style={{
                    background: "#F7F4EE",
                    color: "#2F496E",
                    borderColor: "rgba(47,73,110,0.2)",
                  }}
                >
                  <span
                    className="inline-flex h-1 w-1 rounded-full"
                    style={{ background: ACCENT }}
                  />
                  {t}
                </span>
              ))}
            </div>
          )}

          <div className="mt-8 flex flex-col items-start gap-3">
            <a
              href={item.betaUrl}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="projet-modal-beta-cta"
              className="inline-flex items-center gap-2 rounded-full bg-up-brown text-up-offwhite px-7 py-3.5 text-sm font-medium tracking-wide transition-all duration-500 hover:-translate-y-0.5 hover:shadow-[0_18px_40px_-18px_rgba(47,73,110,0.55)]"
            >
              Voir la version bêta
              <ExternalLink className="h-4 w-4" strokeWidth={1.8} />
            </a>
            <p
              className="text-[12px] italic"
              style={{ color: MUTED }}
            >
              Site actuellement en cours de finalisation
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function Galerie() {
  const [tab, setTab] = useState("realisations");
  const [openProjet, setOpenProjet] = useState(null);

  const featured = projetsEnCours[0];

  return (
    <div data-testid="galerie-page" style={{ backgroundColor: CREAM }}>
      {/* HEADER */}
      <section className="px-5 sm:px-6 md:px-8 pt-14 sm:pt-16 md:pt-20 pb-6 md:pb-8">
        <div className="max-w-3xl mx-auto text-center reveal">
          <span
            className="inline-block text-[11px] sm:text-xs tracking-[0.32em] uppercase font-medium"
            style={{ color: LEATHER }}
          >
            La galerie
          </span>
          <h1
            className="mt-4 font-serif text-[2.25rem] sm:text-5xl md:text-[3.25rem] leading-[1.04] tracking-tight"
            style={{ color: BORDEAUX }}
          >
            Quelques
            <em className="italic font-normal" style={{ color: ACCENT }}>
              {" "}
              créations.
            </em>
          </h1>
        </div>
      </section>

      {/* PROJET VEDETTE — Camping Les Tamaris */}
      {featured && (
        <section
          className="px-5 sm:px-6 md:px-8 pb-10 md:pb-14"
          data-testid="galerie-featured"
        >
          <div className="max-w-6xl mx-auto">
            <div className="mb-4 flex items-center justify-center gap-3">
              <span
                className="h-px w-10"
                style={{ background: "rgba(47,73,110,0.25)" }}
              />
              <span
                className="text-[11px] sm:text-xs tracking-[0.32em] uppercase font-medium"
                style={{ color: LEATHER }}
              >
                Projet à la une
              </span>
              <span
                className="h-px w-10"
                style={{ background: "rgba(47,73,110,0.25)" }}
              />
            </div>

            <article
              className="group relative overflow-hidden rounded-3xl ring-1 shadow-[0_40px_90px_-40px_rgba(47,73,110,0.4)]"
              style={{
                background: "#F7F4EE",
                borderColor: "rgba(47,73,110,0.18)",
              }}
            >
              <div className="grid md:grid-cols-5">
                {/* Visuel */}
                <button
                  type="button"
                  onClick={() => setOpenProjet(featured)}
                  data-testid="galerie-featured-image"
                  className="relative md:col-span-3 overflow-hidden aspect-[16/10] md:aspect-auto md:min-h-[420px]"
                >
                  <img
                    src={featured.image}
                    alt={featured.name}
                    className="w-full h-full object-cover transition-transform duration-[900ms] group-hover:scale-[1.04]"
                    loading="lazy"
                  />
                  {/* Badge En cours */}
                  <div
                    className="float-soft-delay absolute top-5 left-5 inline-flex items-center gap-2 rounded-full px-3.5 py-1.5 text-[10px] tracking-[0.26em] uppercase font-medium shadow-[0_14px_30px_-12px_rgba(47,73,110,0.5)] ring-1"
                    style={{
                      background: "#F7F4EE",
                      color: "#2F496E",
                      borderColor: "rgba(47,73,110,0.18)",
                    }}
                  >
                    <span className="inline-flex h-1.5 w-1.5 rounded-full bg-amber-500 animate-pulse" />
                    En cours
                  </div>
                  {/* Mention bêta */}
                  <div
                    className="absolute bottom-5 left-5 inline-flex items-center gap-2 rounded-full px-3.5 py-1.5 text-[11px] font-medium ring-1 backdrop-blur"
                    style={{
                      background: "rgba(47,73,110,0.9)",
                      color: "#F7F4EE",
                      borderColor: "rgba(220,198,163,0.4)",
                    }}
                  >
                    <Globe className="h-3.5 w-3.5" />
                    Site en bêta disponible
                  </div>
                </button>

                {/* Contenu */}
                <div className="md:col-span-2 p-7 sm:p-9 md:p-10 flex flex-col justify-center">
                  <span
                    className="text-[11px] tracking-[0.28em] uppercase font-medium"
                    style={{ color: MUTED }}
                  >
                    {featured.tagline}
                  </span>
                  <h2
                    className="mt-3 font-serif text-[1.75rem] sm:text-3xl md:text-[2.25rem] leading-[1.08] tracking-tight"
                    style={{ color: BORDEAUX }}
                  >
                    {featured.name}.
                  </h2>
                  <p
                    className="mt-2 font-serif italic text-base md:text-lg"
                    style={{ color: ACCENT }}
                  >
                    {featured.subtitle}
                  </p>

                  <p
                    className="mt-5 text-[14px] md:text-[14.5px] leading-[1.75]"
                    style={{ color: TEXT }}
                  >
                    Refonte complète du site : nouvelle identité, navigation
                    repensée, mise en valeur des hébergements et réservation
                    en ligne intégrée.
                  </p>

                  {featured.tags && (
                    <div className="mt-5 flex flex-wrap gap-2">
                      {featured.tags.slice(0, 3).map((t) => (
                        <span
                          key={t}
                          className="inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-[11px] tracking-wide ring-1"
                          style={{
                            background: "#FFFFFF",
                            color: "#2F496E",
                            borderColor: "rgba(47,73,110,0.2)",
                          }}
                        >
                          <span
                            className="inline-flex h-1 w-1 rounded-full"
                            style={{ background: ACCENT }}
                          />
                          {t}
                        </span>
                      ))}
                    </div>
                  )}

                  <div className="mt-7 flex flex-col sm:flex-row gap-3">
                    <a
                      href={featured.betaUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid="galerie-featured-beta-cta"
                      className="inline-flex items-center justify-center gap-2 rounded-full bg-up-brown text-up-offwhite px-6 py-3 text-sm font-medium tracking-wide transition-all duration-500 hover:-translate-y-0.5 hover:shadow-[0_18px_40px_-18px_rgba(47,73,110,0.55)]"
                    >
                      Voir la version bêta
                      <ExternalLink className="h-4 w-4" strokeWidth={1.8} />
                    </a>
                    <button
                      type="button"
                      onClick={() => setOpenProjet(featured)}
                      data-testid="galerie-featured-details"
                      className="inline-flex items-center justify-center gap-2 rounded-full px-6 py-3 text-sm font-medium tracking-wide ring-1 transition-all duration-500 hover:-translate-y-0.5"
                      style={{
                        color: BORDEAUX,
                        borderColor: "rgba(47,73,110,0.3)",
                        background: "transparent",
                      }}
                    >
                      En savoir plus
                      <ArrowUpRight className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              </div>
            </article>
          </div>
        </section>
      )}

      {/* ONGLETS */}
      <section className="px-5 sm:px-6 md:px-8">
        <div
          className="max-w-md mx-auto flex items-center gap-1 rounded-full p-1 ring-1"
          data-testid="galerie-tabs"
          style={{ background: "rgba(220,198,163,0.22)", borderColor: "rgba(47,73,110,0.18)" }}
          role="tablist"
        >
          {[
            { id: "realisations", label: "Réalisations" },
            { id: "en-cours", label: "Projets en cours" },
          ].map((t) => {
            const active = tab === t.id;
            return (
              <button
                key={t.id}
                type="button"
                role="tab"
                aria-selected={active}
                onClick={() => setTab(t.id)}
                data-testid={`galerie-tab-${t.id}`}
                className={`flex-1 rounded-full px-4 py-2.5 text-[12px] sm:text-sm font-medium tracking-wide transition-all duration-500 ${
                  active
                    ? "bg-up-brown text-up-offwhite shadow-[0_12px_30px_-12px_rgba(47,73,110,0.5)]"
                    : "text-up-brown hover:text-up-brown-soft"
                }`}
              >
                {t.label}
              </button>
            );
          })}
        </div>
      </section>

      {/* CONTENU */}
      <section
        className="px-5 sm:px-6 md:px-8 pt-10 md:pt-14 pb-16 md:pb-20"
        data-testid="galerie-content"
      >
        <div className="max-w-5xl mx-auto">
          {tab === "realisations" && (
            <div
              className="grid sm:grid-cols-2 gap-6 md:gap-8"
              data-testid="galerie-grid-realisations"
            >
              {realisations.map((item) => (
                <RealisationCard key={item.id} item={item} />
              ))}
            </div>
          )}

          {tab === "en-cours" && (
            <div
              className="grid sm:grid-cols-2 gap-6 md:gap-8"
              data-testid="galerie-grid-en-cours"
            >
              {projetsEnCours.map((item) => (
                <ProjetEnCoursCard
                  key={item.id}
                  item={item}
                  onOpen={() => setOpenProjet(item)}
                />
              ))}
            </div>
          )}
        </div>
      </section>

      {/* CTA FINAL */}
      <section
        className="px-5 sm:px-6 md:px-8 pt-6 pb-20 md:pb-28"
        data-testid="galerie-cta"
      >
        <div
          className="max-w-3xl mx-auto rounded-3xl text-center px-6 sm:px-10 py-14 md:py-16 ring-1"
          style={{
            background: "#EFE2C9",
            borderColor: "rgba(47,73,110,0.18)",
          }}
        >
          <span
            className="text-[11px] tracking-[0.32em] uppercase"
            style={{ color: LEATHER }}
          >
            Et si on parlait ?
          </span>
          <h2
            className="mt-5 font-serif text-[1.75rem] sm:text-3xl md:text-[2.25rem] leading-[1.1] tracking-tight"
            style={{ color: BORDEAUX }}
          >
            Votre projet
            <em className="italic font-normal" style={{ color: ACCENT }}>
              {" "}
              mérite d&apos;exister ici.
            </em>
          </h2>
          <p className="mt-5 text-[14px] md:text-[15px]" style={{ color: TEXT }}>
            Discutons ensemble de ce qui vous ressemble.
          </p>
          <Link
            to="/contact"
            data-testid="galerie-cta-link"
            className="mt-8 inline-flex items-center gap-2 rounded-full bg-up-brown text-up-offwhite px-8 py-3.5 text-sm font-medium tracking-wide transition-all duration-500 hover:-translate-y-0.5 hover:shadow-[0_18px_40px_-18px_rgba(47,73,110,0.55)]"
          >
            Discutons de votre projet
            <ArrowUpRight className="h-4 w-4" />
          </Link>
        </div>
      </section>

      {openProjet && (
        <ProjetModal item={openProjet} onClose={() => setOpenProjet(null)} />
      )}
    </div>
  );
}
